import { FC, useEffect, useState } from 'react';
import { CustomButton, CustomModal, GenericOTP, If } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { VoidFn } from 'types/baseTypes';
import { useCountDownInterval } from 'hooks';
import { AccountSettingsFlowTypes, VerificationTypes } from '../types';
import { useRequestOTPMutation } from 'containers/auth/api';
import { RequestOTPPayload } from 'containers/auth/auth.model';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsMyAccount } from '../events';
import FormCloseReactIcon from 'assets/react-icons/FormCloseReactIcon';

interface VerifyAccountModalProps {
  className?: string;
  open?: boolean;
  toggleOpen?: VoidFn;
  otpError?: string;
  onVerifyOTP?: (otp: string) => void;
  isLoading?: boolean;
  phoneNumber: string;
  email: string;
  methodType: VerificationTypes;
  currentFlow: AccountSettingsFlowTypes;
  setOtpError: React.Dispatch<React.SetStateAction<string>>;
}

const VerifyAccountModal: FC<VerifyAccountModalProps> = ({
  toggleOpen,
  open,
  otpError = '',
  onVerifyOTP,
  isLoading,
  phoneNumber,
  email,
  methodType,
  currentFlow,
  setOtpError
}) => {
  const [otp, setOtp] = useState('');
  const [resendEnabled, setResendEnabled] = useState(false);

  const { seconds, resetTimer } = useCountDownInterval({
    countDownCompletedCallback: () => setResendEnabled(true),
    countDownTime: 30
  });

  const [requestOTPMutation, { isSuccess: isRequestOTPSuccess }] = useRequestOTPMutation();

  const resendOTP = () => {
    let payload: RequestOTPPayload = null;
    if (methodType === VerificationTypes.EMAIL) {
      payload = {
        ...(currentFlow === AccountSettingsFlowTypes.RESET_PASSWORD ? { resetPassword: true } : { verifyEmail: true }),
        email: email
      };
    } else {
      payload = {
        ...(currentFlow === AccountSettingsFlowTypes.RESET_PASSWORD ? { resetPassword: true } : { verifyEmail: true }),
        phone: phoneNumber
      };
    }
    requestOTPMutation(payload);
  };

  useEffect(() => {
    if (isRequestOTPSuccess) {
      setResendEnabled(false);
      resetTimer();
    }
  }, [isRequestOTPSuccess]);

  const onCancelClick = () => {
    toggleOpen();
    logAnalyticEvent(CleverTapEventsMyAccount.webMyAccVerificationBackClick, {});
  };

  const handleOTPChange = otpString => {
    setOtpError('');
    setOtp(otpString);
    if (otpString.length === 4) {
      onVerifyOTP(otpString);
      logAnalyticEvent(CleverTapEventsMyAccount.webMyAccVerificationContinueClick, { flow: currentFlow });
    }
  };

  const handleVerifyClick = () => {
    onVerifyOTP(otp);
    logAnalyticEvent(CleverTapEventsMyAccount.webMyAccVerificationContinueClick, { flow: currentFlow });
  };

  return (
    <CustomModal width="rounded md:w-[452px] min-h-[347px]" open={open} closeModal={onCancelClick}>
      <div className="w-full p-10">
        <div className="flex flex-col items-center justify-center">
          <FormCloseReactIcon
            id="my-account-verify-account-modal-close"
            height="24"
            width="24"
            className="absolute top-2 right-2 shrink-0 cursor-pointer"
            onClick={onCancelClick}
          />
          <div className="flex w-full flex-col">
            <div className="text-xl font-bold leading-6 text-heading">Verify your account</div>
            <If condition={methodType === VerificationTypes.EMAIL}>
              <div>
                <div className="mt-3 text-sbase font-normal text-primaryText">
                  Enter the code we just sent to your email
                </div>
                <div className="mb-9 text-xl font-bold text-primaryText">{email}</div>
              </div>
            </If>
            <If condition={methodType === VerificationTypes.TEXT}>
              <div className="mt-3 mb-10 text-17px font-normal text-primaryText">
                Enter the code sent to your registered number
              </div>
            </If>
            <div className={`relative flex flex-col gap-10 ${otpError ? 'mb-9' : 'mb-5'}`}>
              <GenericOTP
                separator={<span className="w-11"></span>}
                otpInputStyle={`emailOtpStyle !w-[60px] border-none focus:border-none 
                focus:outline-none placeholder:text-accentText placeholder:font-bold placeholder:text-xl
                text-primaryText text-xl font-bold focus-visible:placeholder:text-transparent
                ${otpError ? '!border-error !border-[1px] !border-solid focus:border-error' : ''}`}
                onChange={handleOTPChange}
                value={otp}
              />
              <If condition={Boolean(otpError)}>
                <div className="absolute top-16 text-sbase text-error">{otpError}</div>
              </If>
            </div>
            <div className="flex">
              <If condition={resendEnabled}>
                <div className="text-sbase text-primaryText">Trouble receiving the code?</div>
                <div
                  onClick={resendOTP}
                  className={`ml-3 cursor-pointer text-sbase text-secondary 
              underline underline-offset-2`}>
                  Resend code
                </div>
              </If>
              <If condition={!resendEnabled}>
                <div className="text-sbase text-primaryText">
                  {`Resend the code in 
                ${seconds < 10 ? `0${seconds}` : seconds} seconds`}
                </div>
              </If>
            </div>
          </div>
        </div>
        <div className="flex flex-row items-end justify-end gap-5">
          <CustomButton
            id="verify-email-modal-back-button"
            className="mt-10 h-[38px] w-[100px]"
            type={ButtonType.SECONDARY}
            onClick={onCancelClick}>
            Back
          </CustomButton>
          <CustomButton
            id="verify-email-modal-continue-button"
            isLoading={isLoading}
            onClick={handleVerifyClick}
            disabled={!otp || otp.length !== 4}
            className={`text-semibold mt-5 h-[38px] w-[150px] rounded-md bg-primaryBtn
            text-px13 text-primaryBtnText shadow-none hover:bg-primaryButtonHover`}>
            Continue
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default VerifyAccountModal;
